import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

/**
 * ADR-015 Fase 2: lista colaborativa de acoes do proximo passo por oferta.
 * Substitui o briefing.proximo_passo (texto unico) por items granulares com
 * autoria (agent|human), prioridade, prazo e ciclo de vida (pendente -> feito|rejeitado).
 */
export const useProximoPassoItems = (ofertaId) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!ofertaId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase
      .from('proximo_passo_items')
      .select('*')
      .eq('oferta_id', ofertaId)
      .order('created_at', { ascending: false });
    if (err) setError(err);
    else setItems(data || []);
    setLoading(false);
  }, [ofertaId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const addItem = useCallback(async ({ acao, prazo_sugerido, prioridade = 'media', autor_id }) => {
    if (!ofertaId || !autor_id || !acao?.trim()) {
      return { error: { message: 'ofertaId, autor_id e acao sao obrigatorios' } };
    }
    const { data, error: err } = await supabase
      .from('proximo_passo_items')
      .insert({
        oferta_id: ofertaId,
        acao: acao.trim(),
        prazo_sugerido: prazo_sugerido?.trim() || null,
        prioridade,
        autor_tipo: 'human',
        autor_id,
        status: 'pendente',
      })
      .select()
      .single();
    if (!err && data) {
      setItems((prev) => [data, ...prev]);
    }
    return { data, error: err };
  }, [ofertaId]);

  const updateStatus = useCallback(async (itemId, newStatus, changedBy) => {
    const nowIso = new Date().toISOString();
    const { error: err } = await supabase
      .from('proximo_passo_items')
      .update({
        status: newStatus,
        status_changed_at: nowIso,
        status_changed_by: changedBy || null,
      })
      .eq('id', itemId);
    if (!err) {
      setItems((prev) => prev.map((it) =>
        it.id === itemId
          ? { ...it, status: newStatus, status_changed_at: nowIso, status_changed_by: changedBy || null }
          : it
      ));
    }
    return { error: err };
  }, []);

  const editItem = useCallback(async (itemId, updates) => {
    const allowed = (({ acao, prazo_sugerido, prioridade }) => ({ acao, prazo_sugerido, prioridade }))(updates);
    const cleaned = Object.fromEntries(Object.entries(allowed).filter(([, v]) => v !== undefined));
    if (Object.keys(cleaned).length === 0) return { error: null };
    const { error: err } = await supabase
      .from('proximo_passo_items')
      .update(cleaned)
      .eq('id', itemId);
    if (!err) {
      setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...cleaned } : it)));
    }
    return { error: err };
  }, []);

  const deleteItem = useCallback(async (itemId) => {
    const { error: err } = await supabase
      .from('proximo_passo_items')
      .delete()
      .eq('id', itemId);
    if (!err) {
      setItems((prev) => prev.filter((it) => it.id !== itemId));
    }
    return { error: err };
  }, []);

  const pendentes = useMemo(
    () => items.filter((i) => i.status === 'pendente' || i.status === 'em_andamento'),
    [items]
  );
  const concluidos = useMemo(
    () => items.filter((i) => i.status === 'feito' || i.status === 'rejeitado'),
    [items]
  );

  return {
    items,
    pendentes,
    concluidos,
    loading,
    error,
    addItem,
    updateStatus,
    editItem,
    deleteItem,
    refetch: fetchItems,
  };
};

export default useProximoPassoItems;
